/**
 * The Personal / Team / Enterprise catalog (plan 017). The one place that says what each self-serve tier
 * costs and how many credits it includes, used by checkout, webhooks, onboarding and the Billing page.
 * Only read when PRICING_V2 is on; with it off the old credits × rate pricing applies.
 */

export type PlanTier = "personal" | "team" | "enterprise";
/** Enterprise is created by an admin, never bought on the page */
export type SelfServePlanTier = "personal" | "team";
export type PaymentAccount = "razorpay_v1" | "razorpay_v2" | "polar";
export type PlanCurrency = "INR" | "USD";
export type AddonKind = "inbox" | "messages";

export interface SelfServePlan {
  tier: SelfServePlanTier;
  name: string;
  monthlyCredits: number;
  /** Monthly price before GST, in paise / cents */
  priceMinor: Record<PlanCurrency, number>;
}

export const SELF_SERVE_PLAN_TIERS: readonly SelfServePlanTier[] = ["personal", "team"];

export const PRICING_PLANS: Record<SelfServePlanTier, SelfServePlan> = {
  personal: {
    tier: "personal",
    name: "Personal",
    monthlyCredits: 1499,
    priceMinor: { INR: 149_900, USD: 1_500 },
  },
  team: {
    tier: "team",
    name: "Team",
    monthlyCredits: 4999,
    priceMinor: { INR: 499_900, USD: 4_900 },
  },
};

/** A missing or unknown plan on a signup link means Team (plan 017, "Signup link contract") */
export const DEFAULT_PLAN_TIER: SelfServePlanTier = "team";

export interface PricingAddon {
  kind: AddonKind;
  name: string;
  /** Monthly price per unit before GST, in paise / cents */
  priceMinor: Record<PlanCurrency, number>;
}

export const PRICING_ADDONS: Record<AddonKind, PricingAddon> = {
  inbox: { kind: "inbox", name: "Extra inbox", priceMinor: { INR: 100_000, USD: 1_000 } },
  messages: { kind: "messages", name: "Extra 5,000 messages per day", priceMinor: { INR: 200_000, USD: 2_000 } },
};

export const ADDON_KINDS: readonly AddonKind[] = ["inbox", "messages"];
/** Upper bound on one add-on's quantity, so a typo cannot put a huge charge on a mandate */
export const MAX_ADDON_QUANTITY = 50;

export function isSelfServePlanTier(value: unknown): value is SelfServePlanTier {
  return value === "personal" || value === "team";
}

export function isPlanTier(value: unknown): value is PlanTier {
  return value === "personal" || value === "team" || value === "enterprise";
}

export function isAddonKind(value: unknown): value is AddonKind {
  return value === "inbox" || value === "messages";
}

/** The tier a signup link or checkout request asks for: anything other than personal / team is Team */
export function resolveSelfServePlanTier(value: string | null | undefined): SelfServePlanTier {
  const normalized = (value || "").trim().toLowerCase();
  return isSelfServePlanTier(normalized) ? normalized : DEFAULT_PLAN_TIER;
}

export function getSelfServePlan(tier: SelfServePlanTier): SelfServePlan {
  return PRICING_PLANS[tier];
}

/** Ordering for upgrade vs downgrade: a higher rank is the bigger plan */
export function getPlanTierRank(tier: SelfServePlanTier): number {
  return tier === "team" ? 2 : 1;
}

/**
 * Whether connected WhatsApp numbers add the prorated 3,000 "seat credits" each. Personal and Team sell
 * extra inboxes as an add-on instead, so they get none. Legacy rows (no tier) and grandfathered Enterprise
 * rows keep them, so their allowance does not drop at the cutover.
 */
export function shouldGrantSeatCredits(planTier: string | null | undefined): boolean {
  return planTier !== "personal" && planTier !== "team";
}

export function isValidAddonQuantity(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= MAX_ADDON_QUANTITY;
}

export function formatPlanPrice(amountMinor: number, currency: PlanCurrency): string {
  return currency === "INR"
    ? `₹${(amountMinor / 100).toLocaleString("en-IN")}`
    : `$${(amountMinor / 100).toLocaleString("en-US")}`;
}
